import { formatUnits, parseUnits } from 'viem';
import { Gwei, GWEI_BASE, GWEI_UNIT } from './gwei';

/**
 * Multiply two numbers with high precision.
 * This function can also be used to apply a factor (e.g., percentage as decimal).
 */
export function multiply(a: bigint, b: bigint) {
  return Gwei.mul(a, b);
}

/**
 * Divide two numbers with high precision.
 * Throws an error if attempting to divide by zero.
 */
export function divide(a: bigint, b: bigint) {
  if (b === 0n) throw new Error('Division by zero');
  return Gwei.div(a, b);
}

/**
 * Divide two numbers, returning 0 if the divisor is 0.
 */
export function safeDivide(a: bigint, b: bigint) {
  return b === 0n ? 0n : Gwei.div(a, b);
}

/**
 * Add two numbers with high precision.
 */
export function add(a: bigint, b: bigint) {
  return a + b;
}

/**
 * Subtract two numbers with high precision.
 */
export function subtract(a: bigint, b: bigint) {
  return a - b;
}

/**
 * Multiply a bigint by a decimal.
 *
 * @example
 *    multiplyByDecimal(Gwei.toGwei(100), 1 / 3))
 *    > 33333333333n // equivalent to Gwei.toGwei("33.333333333")
 */
export function multiplyByDecimal(value: bigint, decimal: number | string): bigint {
  // Temporarily use double precision to avoid losing precision
  const fraction = parseUnits(decimal.toString(), GWEI_BASE * 2);
  return divide(multiply(value, fraction), BigInt(GWEI_UNIT) * BigInt(GWEI_UNIT));
}

/**
 * Divide two bigints and return the decimal result, useful for calculating
 * ratios/percentages.
 *
 * @example
 *    divideToDecimal(Gwei.toGwei(1), Gwei.toGwei(3))
 *    > 0.333333333
 *    divideToDecimal(Gwei.toGwei('0.000000001'), Gwei.toGwei('1000000000'))
 *    > 1e-18
 */
export const divideToDecimal = (a: bigint, b: bigint): number => {
  if (b === 0n) throw new Error('Division by zero');

  // First try with greater precision, falling back to limited precision.
  const scaledResult = (a * BigInt(GWEI_UNIT * GWEI_UNIT)) / b;
  return scaledResult < Number.MAX_SAFE_INTEGER && scaledResult > Number.MIN_SAFE_INTEGER
    ? Number(scaledResult) / (GWEI_UNIT * GWEI_UNIT)
    : Number(divide(a, b)) / GWEI_UNIT;
};

/**
 * Round a number to a specified number of decimal places.
 * This function handles both positive and negative numbers correctly.
 */
export function round(value: number, decimalPlaces: number): number {
  const multiplier = 10 ** decimalPlaces;
  return (Math.sign(value) * Math.round(Math.abs(value) * multiplier)) / multiplier;
}

export function etherToWei(etherValue: number): bigint {
  return parseUnits(etherValue.toString(), GWEI_BASE);
}

export const getDecimalPlaces = (value: bigint): number => {
  // Convert the value to a Gwei-formatted string
  const gweiStr = formatUnits(value, GWEI_BASE);

  // Remove any trailing zeros after decimal point
  const trimmed = gweiStr.replace(/\.?0+$/, '');

  // If no decimal point or only trailing zeros, return 0
  if (!trimmed.includes('.')) return 0;

  // Return length of decimal portion
  return trimmed.split('.')[1]?.length || 0;
};

/**
 * Check if a Gwei value is a multiple of another Gwei value.
 * @param value The Gwei value to check
 * @param multiple The potential Gwei multiple
 * @returns boolean indicating whether value is a multiple
 */
export function isMultipleOf(value: bigint, multiple: bigint): boolean {
  if (multiple === 0n) throw new Error('Cannot check multiples of zero');

  // Since we're already working with Gwei values, we don't need to adjust precision
  return value % multiple === 0n;
}

/**
 * Align a value to the provided size, useful for rounding to the closest
 * lotSize/tickSize.
 *
 * @example
 *    alignToValidSize(Gwei.toGwei(1.234), Gwei.toGwei(0.02))
 *    > 1220000000n // equivalent to Gwei.toGwei("1.22")
 *
 */
export function alignToValidSize(value: bigint, size: bigint): bigint {
  if (size === 0n) return 0n;

  return value - (value % size);
}

/**
 * Clamp a value to within the min and max values, if provided.
 *
 * @example
 *   clamp(5, { min: 10 })
 *   > 10
 *   clamp(53, { min: 90, max: 100 })
 *   > 90
 */
export function clamp(value: bigint, { min, max }: { min?: bigint; max?: bigint }): bigint {
  if (min !== undefined && max !== undefined && min > max) {
    throw new Error(`Error clamping with invalid range. Min: ${min}, max: ${max}.`);
  }
  const clampedMax = max !== undefined ? Gwei.min(value, max) : value;
  const clampedMin = min !== undefined ? Gwei.max(clampedMax, min) : clampedMax;
  return clampedMin;
}

/**
 * Check if a value would be displayed as 0 when formatted with the given decimal places
 * @param value The value to check
 * @param decimalPlaces The number of decimal places that will be used for display
 * @returns true if the absolute value would round to 0 at the given decimal places
 */
export function isEffectivelyZero(value: bigint | undefined, decimalPlaces: number): boolean {
  if (value === undefined || value === 0n) return false;

  const smallestDisplayableUnit = Gwei.toGwei(1 / Math.pow(10, decimalPlaces));
  return Gwei.abs(value) < smallestDisplayableUnit;
}
